/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { Redirect } from 'aurelia-router';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class EditOutcomePage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected outcomeId!: string;
    protected outcome: CM.CarePlanOutcome = { id: null };
    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/careplans/outcomes',
        targetType: 'CM.CarePlanOutcome',
        addTargetProperty: this.addOutcomeProperty.bind(this),
        excludeSystem: false
    };


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: CasePageContext,
        private readonly service: CarePlanService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate({ caseId, goalId, outcomeId }: { caseId: string, goalId: string, outcomeId: string }): Promise<Redirect | true> {
        if(!(caseId && goalId && outcomeId)) {
            throw new Error('Required parameters [caseId] and/or [goalId] and/or [outcomeId] were not found');
        }

        try {
            this.outcomeId = outcomeId;

            if(!this.context.case.number || this.context.case.id !== caseId) {
                await this.context.loadCase(caseId, undefined, ['number']);
            }

            await this.loadOutcome();

            const outcomeCaseId = this.outcome.case?.id;
            if(outcomeCaseId !== caseId) {
                // reroute user if outcome does not belong to the case
                const messageOptions = {
                    what: this.i18n.tr('cm:carePlanOutcome.$'),
                    item: this.i18n.tr('cm:case.$').toLowerCase(),
                    number: this.context.case.number
                };
                const message = this.i18n.tr('ui:messages.errors.doesNotExistFor', messageOptions);
                this.toaster.error().message(message).show();
                return new Redirect('careplan');
            }
            return true;
        } catch(error) {
            throw error;
        }
    }


    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    private addOutcomeProperty(note: CM.CarePlanOutcomeNote): void {
        note.outcome = { id: this.outcome.id };
    }

    public navigateBack(): void {
        this.router.navigateBack();
    }


    ////****************** Service Calls ********************************************////
    private async loadOutcome(): Promise<void> {
        if(!this.outcomeId) return;

        const includes = [
            'case',
            'member',
            'metadata.created.byUser'
        ];

        const fields = [
            'case.id',
            'description',
            'member.name.full',
            'metadata.created.byUser.name.full',
            'metadata.created.timestamp'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadOutcome(this.outcomeId, includes, fields);
            this.outcome = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
